import {
  fetchBaseQuery,
  FetchArgs,
  BaseQueryApi,
  retry,
} from '@reduxjs/toolkit/query';
import storage from 'local-storage-fallback';
import { AUTH, CLIENT_HEADER } from 'src/constants';
import { captureException, addBreadcrumb } from 'src/observability';
import {
  MAX_RETRIES,
  RATE_LIMIT_STATUS,
  RETRY_STATUS,
  TOAST_THRESHOLD_MS,
  computeRetryDelayMs,
  parseRetryAfterMs,
  sleep,
} from 'src/api/retryAfter';
import { setApiError } from 'src/redux/slices/apiErrorSlice';

/**
 * Builds the outbound headers for every RTK Query request. Exported so it can be
 * unit-tested directly (mirrors the `createRetryingBaseQuery` factory below,
 * which is likewise exposed for tests).
 */
export const prepareHeaders = (headers: Headers): Headers => {
  let accessToken;
  let refreshToken;

  try {
    accessToken = JSON.parse(storage.getItem(AUTH.ACCESS_TOKEN));
    refreshToken = JSON.parse(storage.getItem(AUTH.REFRESH_TOKEN));
  } catch (err) {
    // if the token is not a valid json, it's legacy
    accessToken = storage.getItem(AUTH.ACCESS_TOKEN);
    refreshToken = storage.getItem(AUTH.REFRESH_TOKEN);
  }

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  if (refreshToken) {
    headers.set('Refresh-Token', refreshToken);
  }

  headers.set('accept', 'application/json');

  // Stamp every request as web-client traffic (unconditionally — not gated on
  // auth or a flag). See CLIENT_HEADER in src/constants (MKT-455 / TG-2).
  headers.set(CLIENT_HEADER.NAME, CLIENT_HEADER.WEB);

  return headers;
};

const dynamicBaseQuery = fetchBaseQuery({
  baseUrl: process.env.NEXT_PUBLIC_API_URL,
  prepareHeaders,
});

/**
 * Custom base query that handles FormData uploads correctly
 * For FormData: skips Content-Type header (lets fetch set multipart/form-data with boundary)
 * For JSON: sets Content-Type to application/json
 */
export const baseQuery = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>
) => {
  // Detect if the body is FormData
  const isFormData = typeof args !== 'string' && args.body instanceof FormData;

  // For non-FormData requests, ensure Content-Type is set to application/json
  if (!isFormData && typeof args !== 'string') {
    args.headers = {
      'Content-Type': 'application/json',
      ...args.headers,
    };
  }
  // For FormData, don't set Content-Type - fetch will handle it automatically

  return dynamicBaseQuery(args, api, extraOptions);
};

// Per-request retry bookkeeping. Keyed by the (per-dispatch) BaseQueryApi, which
// retry() reuses across attempts, so the entry is naturally scoped to one request
// and garbage-collected with it.
const retryState = new WeakMap<
  BaseQueryApi,
  { startTime: number; attempts: number }
>();

/**
 * Wraps a base query with a bounded retry for shed 503s on idempotent reads.
 * Honors the server `Retry-After` (plus jitter) between attempts and enforces a
 * total-time budget. Mutations and non-503 errors call `retry.fail` to stop
 * immediately. Retry sits below token handling so that runs once on the final
 * settled result. Exposed as a factory so tests can inject a fake inner query.
 * See MKT-293.
 */
export const createRetryingBaseQuery = (innerBaseQuery: typeof baseQuery) =>
  retry(
    async (
      args: string | FetchArgs,
      api: BaseQueryApi,
      extraOptions: Record<string, unknown>
    ) => {
      const state = retryState.get(api) ?? {
        startTime: Date.now(),
        attempts: 0,
      };
      state.attempts += 1;
      retryState.set(api, state);

      const result = await innerBaseQuery(args, api, extraOptions);

      if (!result.error) {
        retryState.delete(api);
        return result;
      }

      // RTK query endpoints (`builder.query`) are idempotent reads even when
      // they POST a large params body; `builder.mutation` endpoints are writes.
      // `api.type` is the authoritative read/write signal — HTTP method is not,
      // since most ES reads POST their query params. Never retry a mutation.
      const is503 = result.error.status === RETRY_STATUS;

      // Detect 429 (including the PARSING_ERROR variant where the real status
      // is on meta.response). Only silently retry when Retry-After is present
      // and short enough (≤ TOAST_THRESHOLD_MS). Long/missing Retry-After
      // falls through to the token handling layer which shows the toast.
      const is429 =
        result.error.status === RATE_LIMIT_STATUS ||
        (result.error.status === 'PARSING_ERROR' &&
          result.meta?.response?.status === RATE_LIMIT_STATUS);

      let isRetryable429 = false;
      if (is429 && api.type === 'query') {
        const retryAfterHeader =
          result.meta?.response?.headers?.get('Retry-After');
        const retryAfterMs = parseRetryAfterMs(retryAfterHeader);
        isRetryable429 =
          retryAfterMs != null && retryAfterMs <= TOAST_THRESHOLD_MS;
      }

      const isRetryable =
        (is503 && api.type === 'query') || isRetryable429;

      // Stop immediately (no sleep) when: not retryable, or we've used our
      // last allowed attempt. Surfaces the error to token handling.
      if (!isRetryable || state.attempts > MAX_RETRIES) {
        retryState.delete(api);
        return retry.fail(result.error, result.meta);
      }

      const retryAfter = result.meta?.response?.headers?.get('Retry-After');
      const delay = computeRetryDelayMs(retryAfter, Date.now() - state.startTime);

      if (delay == null) {
        // Total-time budget exhausted — surface the error.
        retryState.delete(api);
        return retry.fail(result.error, result.meta);
      }

      if (isRetryable429) {
        const url = typeof args === 'string' ? args : (args as FetchArgs).url;
        addBreadcrumb({
          category: 'api',
          message: `Rate limited (429), retrying silently: ${url}`,
          level: 'info',
          data: {
            attempt: state.attempts,
            retryAfterMs: parseRetryAfterMs(retryAfter),
            delayMs: Math.round(delay),
          },
        });
      }

      // Honor Retry-After + jitter ourselves, then let retry() try again (its own
      // backoff is disabled below since we own the between-attempt timing).
      await sleep(delay);
      return result;
    },
    { maxRetries: MAX_RETRIES, backoff: () => Promise.resolve() }
  );

export const baseQueryWithRetry = createRetryingBaseQuery(baseQuery);

/**
 * Factory so tests can inject a mock inner query. The default export
 * (`baseQueryWithTokenHandling`) wires the real `baseQueryWithRetry`.
 */
export const createTokenHandlingBaseQuery = (
  innerBaseQuery: typeof baseQueryWithRetry
) => async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>
) => {
  // When an API error overlay is active (403 account block, 429 rate limit),
  // short-circuit all requests — don't hit the network. Prevents noisy 403/401
  // errors in the console while the overlay is showing. Returns immediately
  // so the error handlers below are never reached.
  const currentState = api.getState() as {
    apiError?: { reason: string | null };
  };
  if (currentState.apiError?.reason) {
    const blockedStatus =
      currentState.apiError.reason === 'rate_limited' ? 429 : 403;
    return {
      error: {
        status: blockedStatus,
        data: { detail: 'Blocked by API error overlay' },
      },
      meta: undefined,
    };
  }

  const result = await innerBaseQuery(args, api, extraOptions);

  if (result.meta?.response?.headers) {
    const headers = result.meta.response.headers;
    const newAccessToken = headers.get('x-access-token');
    const newRefreshToken = headers.get('x-refresh-token');

    // if these tokens are present, its a new refresh and access token to be saved
    if (newAccessToken && newRefreshToken) {
      storage.setItem(AUTH.ACCESS_TOKEN, JSON.stringify(newAccessToken));
      storage.setItem(AUTH.REFRESH_TOKEN, JSON.stringify(newRefreshToken));
    }
  }
  // Detect if API redirected to logout (session expired)
  // This happens when the API returns a logoutUrl instead of expected data
  const data = result.data as { logoutUrl?: string } | undefined;
  if (data?.logoutUrl && typeof window !== 'undefined') {
    window.location.href = '/auth/logout';
  }

  if (result.error) {
    // Handle 403 — only auth/me 403 means "this account cannot use the web
    // app" (MCP trial) and warrants the full-page account_blocked overlay.
    // Other 403s are permission errors on specific resources and should NOT
    // trigger the overlay (STR-844: CIVCO admins blocked from integration tab).
    // A 403 with an empty or non-JSON body arrives as PARSING_ERROR, so also
    // check the underlying response status (same pattern as 429 below).
    const is403 =
      result.error.status === 403 ||
      (result.error.status === 'PARSING_ERROR' &&
        result.meta?.response?.status === 403);

    if (is403) {
      const requestUrl = typeof args === 'string' ? args : args.url;
      const body = result.error.data as { detail?: string } | undefined;
      const isAuthMe = requestUrl?.includes('auth/me');

      addBreadcrumb({
        category: 'api',
        message: `Forbidden (403): ${requestUrl}`,
        level: 'warning',
        data: { detail: body?.detail },
      });

      // Only show the account_blocked overlay for auth/me — the sole endpoint
      // whose 403 means "this account type cannot use the web app at all".
      if (isAuthMe) {
        api.dispatch(
          setApiError({
            reason: 'account_blocked',
            message: body?.detail,
          })
        );
      }
    }

    // 401 handling: no redirect here. AuthorizedLayout's auto-logout effect
    // handles session expiry when useGetCurrentUserQuery returns no user.
    // A baseQuery-level redirect races concurrent 403s (account_blocked) and
    // causes trial users to be redirected before the overlay can show.

    // Handle 429 (rate limited) — surface a full-page error.
    // A 429 with an empty or non-JSON body arrives as PARSING_ERROR, so also
    // check the underlying response status to avoid missing those.
    const is429 =
      result.error.status === 429 ||
      (result.error.status === 'PARSING_ERROR' &&
        result.meta?.response?.status === 429);

    if (is429) {
      const url = typeof args === 'string' ? args : args.url;
      const body = result.error.data as { detail?: string } | undefined;
      const retryAfterHeader =
        result.meta?.response?.headers?.get('Retry-After');
      const retryAfterMs = parseRetryAfterMs(retryAfterHeader);

      addBreadcrumb({
        category: 'api',
        message: `Rate limited (429): ${url}`,
        level: 'warning',
        data: { retryAfterMs, detail: body?.detail },
      });

      api.dispatch(
        setApiError({
          reason: 'rate_limited',
          message: body?.detail,
          ...(retryAfterMs != null && { retryAfterMs }),
        })
      );
    }

    // Log 500 errors and connection timeouts to Sentry + Datadog
    if (result.error.status === 500) {
      const url = typeof args === 'string' ? args : args.url;
      captureException(new Error(`API 500 error: ${url}`), {
        extra: { status: result.error.status, data: result.error.data },
      });
    }

    // Log network/connection errors as breadcrumbs (not full error captures)
    // These are typically client-side network interruptions and not actionable
    if (
      result.error.status === 'FETCH_ERROR' ||
      result.error.status === 'TIMEOUT_ERROR'
    ) {
      const url = typeof args === 'string' ? args : args.url;
      addBreadcrumb({
        category: 'network',
        message: `${result.error.status}: ${url}`,
        level: 'warning',
        data: { status: result.error.status, error: result.error.error },
      });
    }
  }

  return result;
};

export const baseQueryWithTokenHandling =
  createTokenHandlingBaseQuery(baseQueryWithRetry);
